import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useUrlQuery } from 'hooks/useUrlQuery';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';

import { urls } from '@repo/app-constants';

import useFeature from 'services/features/useFeature';
import useMainAccount from 'services/account/useMainAccount';
import useSubscriptions from 'services/general/subscriptionsList';
import { getMainAccountId } from 'store/account';
import { apiName } from 'assets';

import { Header } from 'components';

interface CalendlyEmbedProps {
  url: string;
  prefill?: Record<string, unknown>;
  pageSettings?: Record<string, unknown>;
  utm?: Record<string, unknown>;
}

const CalendlyEmbed: React.FC<CalendlyEmbedProps> = ({
  url,
  prefill,
  pageSettings,
  utm,
}) => {
  const embedContainerRef = useRef<HTMLDivElement>(null);
  const scriptElementRef = useRef<HTMLScriptElement | null>(null);
  const isWidgetInitialized = useRef<boolean>(false);
  const queryClient = useQueryClient();
  const mainAccountId = useSelector(getMainAccountId);

  useEffect(() => {
    const scriptId = 'calendly-script';

    const initWidget = () => {
      if (
        window.Calendly &&
        embedContainerRef.current &&
        !isWidgetInitialized.current
      ) {
        window.Calendly.initInlineWidget({
          url:
            url.indexOf('?') > 0
              ? `${url}&hide_event_type_details=1&hide_gdpr_banner=1`
              : `${url}?hide_event_type_details=1&hide_gdpr_banner=1`,
          parentElement: embedContainerRef.current,
          prefill: prefill,
          pageSettings: pageSettings,
          utm: utm,
        });
        isWidgetInitialized.current = true; // Mark as initialized to prevent duplication
      }
    };

    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.id = scriptId;
      script.onload = initWidget;
      document.body.appendChild(script);
      scriptElementRef.current = script;
    } else if (!isWidgetInitialized.current) {
      // If script exists but widget is not initialized, try initializing.
      initWidget();
    }

    return () => {
      // Cleanup Calendly's iframe and the script to prevent re-initialization issues
      if (embedContainerRef.current) {
        embedContainerRef.current.innerHTML = '';
      }
      if (
        scriptElementRef.current &&
        document.body.contains(scriptElementRef.current)
      ) {
        document.body.removeChild(scriptElementRef.current);
        scriptElementRef.current = null;
      }
      isWidgetInitialized.current = false;

      // Force reload of consults when component unmounts (user might have booked a new consult)
      queryClient.invalidateQueries({
        queryKey: [apiName, 'consultsList', mainAccountId],
      });
    };
  }, [url, prefill, pageSettings, utm]);

  return (
    <div ref={embedContainerRef} style={{ minWidth: 320, height: '80vh' }} />
  );
};

const useConsultLink = (method?: string | null) => {
  const { currentKitId } = useCurrentKitAndTinyAccount();
  const { data: mainAccountData } = useMainAccount();
  const { isAnyTinyPlus } = useSubscriptions();
  const { isFeatureEnabled: showGroupCalls } = useFeature(
    'consult_group_calls',
  );

  const consultLink = useMemo(() => {
    const query = new URLSearchParams();
    const isGroupCall = method === 'group';
    const consultCreditsCount = mainAccountData?.consult_credits_count ?? 0;

    if (mainAccountData?.email) {
      query.append('email', mainAccountData.email);
    }
    if (currentKitId) {
      query.append('a1', currentKitId);
    }
    if (mainAccountData?.phone_number && !isGroupCall) {
      const phoneParam = consultCreditsCount > 0 ? 'a6' : 'a5';
      query.append(phoneParam, mainAccountData.phone_number);
    }

    let url = '';
    if (isGroupCall && showGroupCalls) {
      url = urls.CONSULTATION_CALENDLY_GROUP;
    } else {
      // Logic for 1:1 calls
      if (consultCreditsCount > 0) {
        url = urls.CONSULTATION_FREE_CALENDLY;
      } else if (isAnyTinyPlus) {
        url = urls.CONSULTATION_CALENDLY;
      } else {
        url = urls.CONSULTATION_CALENDLY_FULL_PRICE;
      }
    }

    const queryString = query.toString();
    return queryString ? `${url}?${queryString}` : url;
  }, [currentKitId, mainAccountData, isAnyTinyPlus, method, showGroupCalls]);

  return consultLink;
};

const ConsultsBooking = () => {
  const navigate = useNavigate();
  const { kitId } = useParams<{ kitId: string }>();
  const query = useUrlQuery();
  const cancellation = query.get('cancellation');
  const rescheduling = query.get('rescheduling');

  const { isFeatureEnabled: showGroupCalls } = useFeature(
    'consult_group_calls',
  );
  const method = query.get('method');

  useEffect(() => {
    if (showGroupCalls && !method && !cancellation && !rescheduling) {
      navigate(`/results/${kitId}/consults/select-method`, { replace: true });
    }
  }, [method, cancellation, rescheduling, navigate, kitId]);
  const consultLink = useConsultLink(method);
  const { isFeatureEnabled: showV2, variables } = useFeature('consults_v2');

  const calendlyLink = useMemo(() => {
    if (cancellation) {
      return `https://calendly.com/cancellations/${cancellation}`;
    }
    if (rescheduling) {
      return `https://calendly.com/reschedulings/${rescheduling}`;
    }

    const prefillQuery = consultLink.substring(consultLink.indexOf('?'));

    // For both 1:1 and group calls, the consultLink from the hook now provides the complete URL.
    // A separate feature flag is used to override with a test link if needed.
    if (showV2 && variables?.use_calendly_test_link) {
      if (
        showGroupCalls &&
        method === 'group' &&
        typeof variables?.calendly_group_test_link === 'string'
      ) {
        return `${variables.calendly_group_test_link}${prefillQuery}`;
      } else if (typeof variables?.calendly_test_link === 'string') {
        return `${variables.calendly_test_link}${prefillQuery}`;
      }
    }

    return consultLink;
  }, [
    cancellation,
    rescheduling,
    consultLink,
    showV2,
    variables,
    showGroupCalls,
    method,
  ]);

  return (
    <>
      <Header title={`Coaching`} />
      <CalendlyEmbed url={calendlyLink} />
    </>
  );
};

export default ConsultsBooking;
